import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, signal, Renderer2, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
// @ts-ignore: CropperJS types may be incomplete, so we ignore type errors for now
import Cropper from 'cropperjs';

@Component({
  selector: 'app-image-crop-upload',
  templateUrl: './image-crop-upload.component.html',
  styleUrls: ['./image-crop-upload.component.scss'],
  standalone: true,
  imports: [CommonModule, ButtonModule, FileUploadModule, DialogModule]
})
export class ImageCropUploadComponent {
  @Input() borderType: 'profile' | 'cover' | 'scholarship' = 'profile';
  @Input() aspectRatio: number = 1;
  @Input() imageUrl: string = '' ;
  @Input() imageClass: string = '' ;
  @Output() imageCropped = new EventEmitter<string>();

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('imageToCrop') imageToCropRef!: ElementRef<HTMLImageElement>;
  @ViewChild('cropperDialog') cropperDialog?: any;
  @ViewChild('primeFileUploader') primeFileUploader?: FileUpload;

  constructor(private messageService: MessageService) {}

  cropper: Cropper | null = null;
  croppingImageUrl = signal<string | null>(null);

  openFileDialog(): void {
    this.fileInputRef.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.croppingImageUrl.set(reader.result as string);
        setTimeout(() => this.openCropper(), 0);
      };
      reader.readAsDataURL(file);
    }
  }

  onFileRemoved() {
    // Clear displayed image
    this.imageUrl = '';

    // Clear cropper image (if modal was open)
    this.croppingImageUrl.set(null);

    // Destroy cropper instance if active
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }

    // Clear input[type=file]
    if (this.fileInputRef) {
      this.fileInputRef.nativeElement.value = '';
    }

    // Clear PrimeNG FileUpload preview (scholarship only)
    if (this.primeFileUploader) {
      try {
        this.primeFileUploader.clear();
      } catch { /* safe ignore */ }
    }

    // Emit empty image to parent
    this.imageCropped.emit('');
  }

  // Handler for PrimeNG FileUpload selection (used when borderType === 'scholarship')
  onPrimeFileSelect(event: any): void {
    const file = event?.files?.[0];
    if (file) {
      if (file.size > 1000000) {
        this.messageService.add({
          severity: 'error',
          summary: 'File too large',
          detail: 'Please upload an image under 1MB'
        });

        // Clear PrimeNG preview
        this.primeFileUploader?.clear();

        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.croppingImageUrl.set(reader.result as string);
        setTimeout(() => this.openCropper(), 0);
      };
      reader.readAsDataURL(file);
      // clear PrimeNG UI preview if available to avoid duplicate previews
      if (this.primeFileUploader) {
        try { this.primeFileUploader.clear(); } catch { /* ignore */ }
      }
    }
  }

  openCropper(): void {
    if (this.cropper) {
      this.cropper.destroy();
    }
    if (!this.imageToCropRef) {
      return;
    }
    // p-dialog visibility is controlled by signal, so just wait for next tick to initialize cropper
    setTimeout(() => {
      const image = this.imageToCropRef.nativeElement;
      this.cropper = new Cropper(image, {
        aspectRatio: this.aspectRatio,
        viewMode: 1,
        autoCropArea: 1,
        responsive: true,
        background: false
      } as any);
    }, 100);
  }

  cropImage(): void {
    if (this.cropper) {
      const canvas = this.cropper.getCroppedCanvas();
      const croppedImage = canvas.toDataURL('image/png');
      this.imageUrl = croppedImage;
      this.imageCropped.emit(croppedImage);
      this.closeCropper();
    }
  }

  closeCropper(): void {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
    this.croppingImageUrl.set(null);
  }

  onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      // Dialog closed, clean up cropper
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      this.croppingImageUrl.set(null);
    }
  }
}
